// Убрать клавиатуру — жестом и кнопкой (24.09.2026).
//
// 🔴 ЗАЧЕМ. Владелец: «на айфоне нет кнопки, чтобы убрать клавиатуру… в
// обычных заметках айфона можно смахнуть вниз, и она уезжает». У нас не было
// ничего: кнопку «Готово» над клавиатурой приложение прячет, жеста нет, а
// нажатие в другое место редактора фокус не снимает. Клавиатура, вызванная
// один раз, оставалась до выхода с экрана и съедала половину заметки.
//
// Два способа, оба — через одну дверь (спрятатьКлавиатуру):
//   • жест как в «Заметках» iPhone: ведёте пальцем по тексту вниз и доводите
//     его до клавиатуры — она уезжает;
//   • кнопка «свернуть» у правого края над клавиатурой — для тех, кто жеста
//     не знает. Видна только пока клавиатура открыта и курсор стоит в тексте
//     редактора (в чате у поля ввода своё место, там кнопка мешала бы).

import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { спрятатьКлавиатуру } from '@/lib/keyboardSync';

/** Насколько язычок заходит под клавиатуру. */
const ЗАХОД_ТОЧЕК = 24;

/** Насколько близко к краю клавиатуры должен дойти палец. */
const КРАЙ_ТОЧЕК = 36;
/** Сколько палец должен пройти вниз, чтобы это считалось жестом, а не касанием. */
const ПУТЬ_ТОЧЕК = 40;

function вРедакторе(): boolean {
    const фокус = document.activeElement as HTMLElement | null;
    return !!фокус?.isContentEditable;
}

export function KeyboardDismiss() {
    const [видно, setВидно] = useState(false);

    // Класс kb-open ставит единый поставщик высоты клавиатуры.
    useEffect(() => {
        const корень = document.documentElement;
        const сверить = () => setВидно(корень.classList.contains('kb-open') && вРедакторе());
        const наблюдатель = new MutationObserver(сверить);
        наблюдатель.observe(корень, { attributes: true, attributeFilter: ['class'] });
        document.addEventListener('focusin', сверить);
        document.addEventListener('focusout', () => setTimeout(сверить, 150));
        сверить();
        return () => {
            наблюдатель.disconnect();
            document.removeEventListener('focusin', сверить);
        };
    }, []);

    // Жест: палец ведут вниз и доводят до верхнего края клавиатуры.
    useEffect(() => {
        let началоY: number | null = null;
        const старт = (e: TouchEvent) => {
            началоY = document.documentElement.classList.contains('kb-open') && вРедакторе()
                ? e.touches[0]?.clientY ?? null : null;
        };
        const ход = (e: TouchEvent) => {
            if (началоY === null) return;
            const y = e.touches[0]?.clientY;
            if (y === undefined || y - началоY < ПУТЬ_ТОЧЕК) return;
            const вв = window.visualViewport;
            const низВидимого = вв ? вв.height + вв.offsetTop : window.innerHeight;
            if (y >= низВидимого - КРАЙ_ТОЧЕК) {
                началоY = null;
                void спрятатьКлавиатуру();
            }
        };
        const конец = () => { началоY = null; };
        document.addEventListener('touchstart', старт, { passive: true });
        document.addEventListener('touchmove', ход, { passive: true });
        document.addEventListener('touchend', конец, { passive: true });
        return () => {
            document.removeEventListener('touchstart', старт);
            document.removeEventListener('touchmove', ход);
            document.removeEventListener('touchend', конец);
        };
    }, []);

    if (!видно) return null;
    // 25.09.2026, по слову владельца: круглая кнопка была крупной и читалась
    // как отдельная панель. Теперь это маленький язычок со стрелкой, будто
    // торчит из самой клавиатуры: прижат к её верхнему краю, низ без рамки.
    return (
        <button
            type="button"
            aria-label="Убрать клавиатуру"
            // Нажатие не должно забирать фокус раньше, чем мы его снимем сами:
            // иначе мелькает меню приложения.
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => void спрятатьКлавиатуру()}
            // Видимый язычок невелик, но область касания — не меньше пальца:
            // прозрачные поля сверху и по бокам.
            //
            // 🪤 25.09.2026: язычок обрывался ровно по верху клавиатуры, а у
            // клавиатуры скруглённые углы — в скруглении светился чёрный
            // треугольник. Теперь (1) язычок продолжается ВНИЗ на ЗАХОД точек и
            // уходит под клавиатуру: клавиатура рисуется поверх страницы, так
            // что хвост прячется за ней при любой её форме; (2) стоит не у
            // самого края, а дальше скругления угла любой клавиатуры.
            className="fixed right-9 z-[10005] px-2 pt-2 flex items-start active:opacity-70"
            style={{ bottom: `calc(var(--kb-h, 0px) - ${ЗАХОД_ТОЧЕК}px)` }}
        >
            <span className="w-11 flex justify-center rounded-t-xl
                             bg-[#2c2c30] border border-b-0 border-white/10 text-white/75"
                  style={{ height: 20 + ЗАХОД_ТОЧЕК, paddingTop: 2 }}>
                <ChevronDown size={16} />
            </span>
        </button>
    );
}
